import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Error403Page from "./Error_backend";
import api from "../api.js";
import Loader from "../components/Loader"; // Import the Loader component

// TypeScript interfaces
interface Appointment {
  date: string;
  doctor?: string;
  type?: string;
}

interface ClinicalHistory {
  mainDiagnosis?: string;
  allergies?: string[];
  notes?: string;
}

interface Patient {
  id: string;
  firstName: string;
  lastName: string;
  name: string;
  registration_date: string;
  status: "active" | "inactive" | "pending";
  avatarInitials: string;
  birth_date: string;
  height: string;
  weight: string;
  age: string;
  sex: string;
  email: string;
  next_appointment?: string;
  last_appointment?: string;
  cellphone: string;
  diseases?: string;
  address?: string;
  occupation?: string;
  clinicalHistory?: ClinicalHistory;
  notes?: string;
}

const PatientDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [patient, setPatient] = useState<Patient | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [generatingReport, setGeneratingReport] = useState<boolean>(false);

  // Alternative version that handles timezone correctly (if needed for specific use cases)
  function formatDateToSpanishWithTimezone(isoString: string): string {
    // Parse the ISO string to get UTC components
    const date = new Date(isoString);

    if (isNaN(date.getTime())) {
      throw new Error("Invalid date string provided");
    }

    // Use UTC methods to avoid timezone issues
    const day = date.getUTCDate();
    const month = date.getUTCMonth();
    const year = date.getUTCFullYear();

    const monthNames = [
      "enero",
      "febrero",
      "marzo",
      "abril",
      "mayo",
      "junio",
      "julio",
      "agosto",
      "septiembre",
      "octubre",
      "noviembre",
      "diciembre",
    ];

    return `${day} de ${monthNames[month]} de ${year}`;
  }

  // Fetch patient data
  useEffect(() => {
    const fetchPatient = async () => {
      if (!id) {
        setError("No patient ID provided");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        const response = await api.get(`/patients/${id}/`);

        if (response.status === 404) {
          throw new Error("Paciente no encontrado");
        }

        if (response.status >= 400) {
          throw new Error(`Error: ${response.status} - ${response.statusText}`);
        }
        const patientData: Patient = response.data;
        setPatient(patientData);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : "Failed to fetch patient data",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchPatient();
  }, [id]);

  // Handle back to patients list
  const handleBackToList = () => {
    navigate("/patients");
  };

  // Handle generate report
  const handleGenerateReport = async () => {
    if (!patient) return;

    try {
      setGeneratingReport(true);

      const response = await api.post(
        `/patients/${patient.id}/make_report/`,
        {},
        {
          responseType: "blob", // Important for file downloads
        },
      );

      // Create blob from response
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.style.display = "none";
      a.href = url;

      // Set the filename for the download
      const filename = `historial_${patient.firstName}_${patient.lastName}_${
        new Date().toISOString().split("T")[0]
      }.pdf`;
      a.download = filename;

      document.body.appendChild(a);
      a.click();

      // Clean up
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : "Error al generar el reporte",
      );
    } finally {
      setGeneratingReport(false);
    }
  };

  // Handle edit patient
  const handleEditPatient = () => {
    if (patient) {
      navigate(`/patient/edit/${patient.id}`);
    }
  };

  // Format date to display in Spanish format
  const formatDate = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return "Fecha no disponible";
      }
      const options: Intl.DateTimeFormatOptions = {
        day: "numeric",
        month: "long",
        year: "numeric",
      };
      return date.toLocaleDateString("es-ES", options);
    } catch {
      return "Fecha no disponible";
    }
  };

  // Format date with time
  const formatDateTime = (dateString: string): string => {
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        return "Fecha no disponible";
      }
      const dateOptions: Intl.DateTimeFormatOptions = {
        weekday: "long",
        day: "numeric",
        month: "long",
      };
      const timeOptions: Intl.DateTimeFormatOptions = {
        hour: "numeric",
        minute: "2-digit",
        hour12: true,
      };

      return `${date.toLocaleDateString(
        "es-ES",
        dateOptions,
      )}, ${date.toLocaleTimeString("es-ES", timeOptions)}`;
    } catch {
      return "Fecha no disponible";
    }
  };

  // Helper function to get display value for optional fields
  const getDisplayValue = (
    value: string | undefined,
    fallback: string = "No especificado",
  ) => {
    return value || fallback;
  };

  // Loading state
  if (loading) {
    return (
      <div className="container agenda-content full-width-main">
        <div className="main-calendar-view patient-detail-view">
          <Loader show={true} />
        </div>
      </div>
    );
  }

  // Error state
  if (error) {
    return (
      <Error403Page
        code={500}
        title="Error al cargar paciente."
        message="Hemos tenido problemas para acceder al recurso."
        homeHref="/"
        loginHref="/login"
        supportHref="/support"
        onHomeClick={(e) => {
          e.preventDefault();
          navigate("/");
        }}
        onLoginClick={(e) => {
          e.preventDefault();
          navigate("/login");
        }}
      />
    );
  }

  // No patient data
  if (!patient) {
    return (
      <div className="container agenda-content full-width-main">
        <div className="main-calendar-view patient-detail-view">
          <div className="empty-state">
            <i className="fas fa-user-slash"></i>
            <h3>Paciente no encontrado</h3>
            <p>No se pudo cargar la información del paciente solicitado.</p>
            <button className="btn btn--primary" onClick={handleBackToList}>
              Volver a la Lista
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="container agenda-content full-width-main"
      id="main-content-patient-detail"
    >
      <div className="main-calendar-view patient-detail-view">
        {/* Header Card */}
        <div className="detail-header-card">
          <div className="patient-profile">
            <div className="profile-avatar large">{patient.avatarInitials}</div>
            <div className="patient-info">
              <h2 className="patient-name">{patient.name}</h2>
              {/* <span className="patient-id">ID: {patient.id}</span> */}
              {/* <span
                className={`badge ${
                  patient.status === "active" ? "accent" : ""
                }`}
              >
                {patient.status === "active"
                  ? "Activo"
                  : patient.status === "inactive"
                  ? "Inactivo"
                  : "Pendiente"}
              </span> */}
            </div>
          </div>
          <div className="detail-actions">
            <button className="btn btn--secondary" onClick={handleBackToList}>
              <i className="fas fa-arrow-left"></i> Volver a la Lista
            </button>
            {/* <button
              className="btn btn--secondary"
              onClick={handleGenerateReport}
              disabled={generatingReport}
            >
              <i
                className={`fas ${
                  generatingReport ? "fa-spinner fa-spin" : "fa-file-pdf"
                }`}
              ></i>
              {generatingReport ? "Generando..." : "Generar Historial"}
            </button> */}
            <button className="btn btn--primary" onClick={handleEditPatient}>
              <i className="fas fa-edit"></i> Editar Paciente
            </button>
          </div>
        </div>

        {/* Detail Grid */}
        <div className="detail-grid">
          {/* Contact Information */}
          <div className="detail-card">
            <h3 className="card-title">
              <i className="fas fa-user-circle"></i> Datos de Contacto
            </h3>

            <div className="form-grid">
              <div className="form-group">
                <label>Nombre Completo</label>
                <p className="detail-value">{patient.name}</p>
              </div>
              <div className="form-group">
                <label>Fecha de Nacimiento</label>
                <p className="detail-value">{formatDate(patient.birth_date)}</p>
              </div>
              <div className="form-group">
                <label>Email</label>
                <p className="detail-value">{patient.email}</p>
              </div>
              <div className="form-group">
                <label>Teléfono</label>
                <p className="detail-value">{patient.cellphone}</p>
              </div>
              <div className="form-group">
                <label>Edad</label>
                <p className="detail-value">{patient.age} años</p>
              </div>
              <div className="form-group">
                <label>Sexo</label>
                <p className="detail-value">
                  {patient.sex === "F"
                    ? "Mujer"
                    : patient.sex === "M"
                      ? "Hombre"
                      : "Otro"}
                </p>
              </div>
              <div className="form-group">
                <label>Peso</label>
                <p className="detail-value">{patient.weight} kg</p>
              </div>
              <div className="form-group">
                <label>Estatura</label>
                <p className="detail-value">{patient.height} cm</p>
              </div>
              <div className="form-group">
                <label>Fecha de registro</label>
                <p className="detail-value">
                  {formatDateToSpanishWithTimezone(patient.registration_date)}
                </p>
              </div>
            </div>
          </div>

          {/* Clinical History */}
          <div className="detail-card">
            <h3 className="card-title">
              <i className="fas fa-notes-medical"></i> Historial clínico
            </h3>

            {patient.next_appointment ? (
              <div className="history-item success">
                <strong>Próxima Cita</strong>
                <span>{formatDateTime(patient.next_appointment)}</span>
              </div>
            ) : (
              <div className="history-item">
                <strong>Próxima Cita</strong>
                <span>No hay citas programadas</span>
              </div>
            )}

            {patient.last_appointment ? (
              <div className="history-item info">
                <strong>Última Cita</strong>
                <span>{formatDateTime(patient.last_appointment)} </span>
              </div>
            ) : (
              <div className="history-item">
                <strong>Última Cita</strong>
                <span>No hay citas previas registradas</span>
              </div>
            )}

            <div className="history-item">
              <strong>Diagnóstico Principal</strong>
              <span>{getDisplayValue(patient.diseases)}</span>
            </div>

            {/* <div className="history-item">
              <strong>Alergias Conocidas</strong>
              {patient.clinicalHistory?.allergies &&
              patient.clinicalHistory.allergies.length > 0 ? (
                <span className="error-text">
                  {patient.clinicalHistory.allergies.map((allergy, index) => (
                    <span key={allergy}>
                      <strong>**{allergy}**</strong>
                      {index < patient.clinicalHistory!.allergies!.length - 1
                        ? ", "
                        : ""}
                    </span>
                  ))}
                </span>
              ) : (
                <span>No se registran alergias conocidas</span>
              )}
            </div> */}

            <div className="history-item full-height">
              <strong>Notas Adicionales</strong>
              <span>
                {getDisplayValue(patient.notes, "No hay notas adicionales")}
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PatientDetail;
